/* Aloe Jewels – rotating reassurance badge (snippets/aj-reassurance.liquid)
 * Rotates the messages every `data-interval` ms and resizes the pill smoothly to fit each one.
 * Pauses while hovered, while off-screen and while the tab is hidden.
 */
(() => {
  if (customElements.get('aj-reassurance')) return;

  const MIN_INTERVAL = 2000;
  const MAX_INTERVAL = 15000;

  const DESKTOP = window.matchMedia('(min-width: 1000px)');

  class AjReassurance extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return; // re-connected because we moved ourselves (see placeInGallery)
      this.initialized = true;
      this.pill = this.querySelector('.aj-reassurance__pill');
      this.allItems = Array.from(this.querySelectorAll('.aj-reassurance__item'));
      if (!this.pill || !this.allItems.length) return;

      this.paused = { hover: false, offscreen: false, hidden: document.hidden };
      this.refreshItems();
      // Messages marked "desktop only" (e.g. the rating, already shown under the photos on mobile)
      this.onBreakpoint = () => { this.refreshItems(); this.schedule(); };
      DESKTOP.addEventListener('change', this.onBreakpoint);

      this.setupGalleryPlacement();

      this.onResize = () => { this.placeInGallery(); this.fit(); };
      window.addEventListener('resize', this.onResize);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.fit());

      const interval = parseInt(this.dataset.interval, 10) || 4000;
      this.interval = Math.min(Math.max(interval, MIN_INTERVAL), MAX_INTERVAL);

      this.onEnter = () => this.setPaused('hover', true);
      this.onLeave = () => this.setPaused('hover', false);
      this.onVisibility = () => this.setPaused('hidden', document.hidden);
      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mouseleave', this.onLeave);
      document.addEventListener('visibilitychange', this.onVisibility);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => this.setPaused('offscreen', !entry.isIntersecting));
        this.observer.observe(this);
      }

      this.schedule();
    }

    disconnectedCallback() {
      if (this.moving) return;
      this.initialized = false;
      if (this.onBreakpoint) DESKTOP.removeEventListener('change', this.onBreakpoint);
      if (this.mediaObserver) this.mediaObserver.disconnect();
      clearTimeout(this.timer);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.removeEventListener('mouseenter', this.onEnter);
      this.removeEventListener('mouseleave', this.onLeave);
      if (this.observer) this.observer.disconnect();
    }

    // Over-the-photos placement: on a desktop GRID gallery the image list is very tall, so we move the badge
    // into the first visible photo. On carousels (mobile, desktop carousel layouts) it stays over the carousel.
    setupGalleryPlacement() {
      if (!this.classList.contains('aj-reassurance--gallery')) return;
      this.home = this.parentElement;
      this.homeNext = this.nextSibling;
      this.carousel = this.home && this.home.querySelector('.product-gallery__carousel');
      if (!this.carousel) return;

      // Variant changes can hide/show photos (Prestige variant image sets) – re-place when that happens
      this.mediaObserver = new MutationObserver(() => this.placeInGallery());
      this.mediaObserver.observe(this.carousel, { attributes: true, attributeFilter: ['hidden'], subtree: true });
      this.placeInGallery();
    }

    placeInGallery() {
      if (!this.carousel) return;
      const isGrid = DESKTOP.matches && getComputedStyle(this.carousel).scrollSnapType.startsWith('none');
      const target = isGrid ? this.carousel.querySelector('.product-gallery__media:not([hidden])') : this.home;
      if (!target || this.parentElement === target) return;

      this.moving = true;
      if (target === this.home) this.home.insertBefore(this, this.homeNext && this.homeNext.parentNode === this.home ? this.homeNext : null);
      else target.appendChild(this);
      this.moving = false;
    }

    // Rebuild the list of messages for the current screen size and restart from the first one
    refreshItems() {
      this.items = this.allItems.filter((item) => !('desktopOnly' in item.dataset) || DESKTOP.matches);
      this.allItems.forEach((item) => {
        item.classList.toggle('is-excluded', !this.items.includes(item));
        item.classList.remove('is-leaving');
      });
      this.index = 0;
      this.items.forEach((item, i) => this.setActive(item, i === 0));
      this.allItems.filter((item) => !this.items.includes(item)).forEach((item) => this.setActive(item, false));
      this.fit();
    }

    setActive(item, active) {
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', active ? 'false' : 'true');
    }

    // Size the pill to the active message so the width animates between messages
    fit() {
      const item = this.items[this.index];
      if (!item) return;
      const style = getComputedStyle(this.pill);
      const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
      this.pill.style.width = `${Math.ceil(item.getBoundingClientRect().width + padding)}px`;
    }

    setPaused(reason, value) {
      this.paused[reason] = value;
      clearTimeout(this.timer);
      if (!Object.values(this.paused).some(Boolean)) this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.items || this.items.length < 2 || Object.values(this.paused).some(Boolean)) return;
      this.timer = setTimeout(() => {
        this.next();
        this.schedule();
      }, this.interval);
    }

    next() {
      const previous = this.items[this.index];
      this.index = (this.index + 1) % this.items.length;
      const current = this.items[this.index];

      this.setActive(previous, false);
      previous.classList.add('is-leaving');
      current.classList.remove('is-leaving');
      this.setActive(current, true);
      this.fit();

      setTimeout(() => previous.classList.remove('is-leaving'), 600);
    }
  }

  customElements.define('aj-reassurance', AjReassurance);
})();
